import { mergeAttributes, Node, nodeInputRule } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, PluginKey } from 'prosemirror-state';
import { TextSelection } from 'prosemirror-state';
import React from 'react';
import KanbanBoardComponent from './KanbanBoardComponent';

// Глобальный счетчик для отслеживания изменений канбана
let globalKanbanUpdateCounter = 0;

// Определение интерфейса для аттрибутов канбан-доски
export interface KanbanBoardAttrs {
  'data-state': any; // Используем только data-state
  updateCounter?: number; // Счетчик обновлений для отслеживания изменений
  id?: string; // ID канбана для уникальной идентификации
}

// Тип для обработчика событий DOM
type DOMEventHandler = (event: Event) => void;

// Функция проверки валидности структуры канбана
function isValidBoardState(state: any): boolean {
  if (!state || typeof state !== 'object') {
    console.warn('[KanbanNode] Проверка: state не является объектом');
    return false;
  }

  // Проверяем наличие обязательных полей
  if (!Array.isArray(state.columns)) {
    console.warn('[KanbanNode] Проверка: state.columns не является массивом');
    return false;
  }

  if (!state.cards || typeof state.cards !== 'object') {
    console.warn('[KanbanNode] Проверка: state.cards не является объектом');
    return false;
  }

  if (!Array.isArray(state.columnOrder)) {
    console.warn('[KanbanNode] Проверка: state.columnOrder не является массивом');
    return false;
  }

  // Проверяем, что все колонки имеют необходимые поля
  for (const column of state.columns) {
    if (!column.id || typeof column.id !== 'string') {
      console.warn('[KanbanNode] Проверка: column.id отсутствует или не является строкой');
      return false;
    }
    if (!column.title || typeof column.title !== 'string') {
      console.warn('[KanbanNode] Проверка: column.title отсутствует или не является строкой');
      return false;
    }
    if (!Array.isArray(column.cardIds)) {
      console.warn('[KanbanNode] Проверка: column.cardIds не является массивом');
      return false;
    }
  }

  // Структура прошла все проверки
  return true;
}

// Функция для безопасного декодирования HTML сущностей
function decodeHtmlEntities(encodedString: string): string {
  try {
    // Используем textarea для декодирования, так как это стандартный и надежный способ
    const textarea = document.createElement('textarea');
    textarea.innerHTML = encodedString;
    return textarea.value;
  } catch (e) {
    console.error("Ошибка при декодировании HTML сущностей:", e);
    return encodedString; // Возвращаем исходную строку в случае ошибки
  }
}

// Функция для объединения нескольких BoardState
function mergeMultipleBoardStates(states: any[]): any {
  if (!states || states.length === 0) return null;
  
  // Если только один state, вернем его
  if (states.length === 1) return states[0];
  
  console.log(`[KanbanNode] Объединение ${states.length} состояний канбана`);
  
  // Создаем базу из первого состояния
  const mergedState = JSON.parse(JSON.stringify(states[0]));
  
  // Набор для отслеживания уже добавленных карточек
  const processedCardIds = new Set<string>();
  
  // Добавляем все карточки из первого состояния в набор
  Object.keys(mergedState.cards || {}).forEach(cardId => {
    processedCardIds.add(cardId);
  });
  
  // Обрабатываем все остальные состояния
  for (let i = 1; i < states.length; i++) {
    const currentState = states[i];
    
    // Пропускаем невалидные состояния
    if (!isValidBoardState(currentState)) {
      console.warn(`[KanbanNode] Состояние #${i+1} невалидно, пропускаем`);
      continue;
    }
    
    // Объединяем карточки
    Object.entries(currentState.cards || {}).forEach(([cardId, card]) => {
      // Добавляем только новые карточки
      if (!processedCardIds.has(cardId)) {
        mergedState.cards[cardId] = card;
        processedCardIds.add(cardId);
        console.log(`[KanbanNode] Добавлена карточка ${cardId} из состояния #${i+1}`);
      }
    });
    
    // Объединяем cardIds в колонках, добавляя только новые
    currentState.columns.forEach((column: any, columnIndex: number) => {
      const targetColumn = mergedState.columns.find((c: any) => c.id === column.id);
      
      if (targetColumn) {
        // Для каждой колонки добавляем только новые cardIds
        column.cardIds.forEach((cardId: string) => {
          if (!targetColumn.cardIds.includes(cardId) && mergedState.cards[cardId]) {
            targetColumn.cardIds.push(cardId);
            console.log(`[KanbanNode] Добавлен cardId ${cardId} в колонку ${column.id}`);
          }
        });
      }
    });
  }
  
  console.log(`[KanbanNode] Объединенное состояние содержит ${Object.keys(mergedState.cards).length} карточек`);
  return mergedState;
}

// Определение узла для канбан-доски
export const InteractiveKanbanNode = Node.create<{
  buttonIcon?: string;
  buttonLabel?: string;
}>({
  name: 'kanbanBoard', // Убедимся, что имя правильное

  group: 'block',

  atom: true, // Узел должен быть атомарным, так как внутреннее содержимое управляется React-компонентом

  draggable: true,

  // Добавление атрибутов
  addAttributes() {
    return {
      'data-type': {
        default: 'kanbanBoard',
        parseHTML: (element) => {
          return element.getAttribute('data-type') || 'kanbanBoard';
        },
        renderHTML: (attributes) => {
          return { 'data-type': 'kanbanBoard' };
        },
      },
      'data-state': {
        default: {
          columns: [
            { id: 'col-1', title: 'Планируется', cardIds: [] },
            { id: 'col-2', title: 'В процессе', cardIds: [] },
            { id: 'col-3', title: 'Завершено', cardIds: [] },
          ],
          cards: {},
          columnOrder: ['col-1', 'col-2', 'col-3'],
          boardTitle: 'Канбан-доска',
        }, // Используем пустой объект по умолчанию без карточек
        parseHTML: (element) => {
          const rawData = element.getAttribute('data-state');
          console.log(`[KanbanNode parseHTML] Сырые данные data-state: ${rawData ? rawData.substring(0, 100) + '...' : 'null'}`);

          if (!rawData) {
            console.warn('[KanbanNode parseHTML] Атрибут data-state отсутствует или пуст. Используется значение по умолчанию.');
            return undefined; // Используем default
          }

          let decodedData: string | null = null;
          try {
            // Шаг 1: Декодируем HTML сущности (например, &quot; -> ")
            decodedData = decodeHtmlEntities(rawData);
            console.log(`[KanbanNode parseHTML] Декодированные данные: ${decodedData ? decodedData.substring(0, 100) + '...' : 'null'}`);

            if (!decodedData) {
               throw new Error("Декодирование вернуло пустую строку");
            }

            // Шаг 2: Проверяем на наличие нескольких JSON структур в строке
            const foundStates: any[] = [];
            let currentPosition = 0;
            
            // Ищем все JSON структуры в строке
            while (currentPosition < decodedData.length) {
              const jsonStartPos = decodedData.indexOf('{', currentPosition);
              if (jsonStartPos === -1) break; // Больше нет JSON структур
              
              let openBraces = 0;
              let foundValidJson = false;
              let endPos = jsonStartPos;
              
              // Анализируем символы, чтобы найти корректное закрытие JSON
              for (let i = jsonStartPos; i < decodedData.length; i++) {
                if (decodedData[i] === '{') openBraces++;
                else if (decodedData[i] === '}') {
                  openBraces--;
                  // Когда нашли закрывающую скобку для начального JSON объекта
                  if (openBraces === 0) {
                    endPos = i + 1; // включая закрывающую скобку
                    foundValidJson = true;
                    break;
                  }
                }
              }
              
              if (foundValidJson) {
                // Извлекаем JSON строку
                const jsonStr = decodedData.substring(jsonStartPos, endPos);
                try {
                  // Пытаемся распарсить извлеченный JSON
                  const parsedData = JSON.parse(jsonStr);
                  
                  // Проверяем структуру на валидность
                  if (isValidBoardState(parsedData)) {
                    foundStates.push(parsedData);
                    console.log(`[KanbanNode parseHTML] Найдена валидная JSON структура #${foundStates.length} длиной ${jsonStr.length} символов`);
                  } else {
                    console.warn(`[KanbanNode parseHTML] Найденная JSON структура не является валидным BoardState`);
                  }
                } catch (err) {
                  console.error(`[KanbanNode parseHTML] Ошибка при парсинге найденной JSON структуры:`, err);
                }
                
                // Двигаемся за текущий JSON для поиска следующего
                currentPosition = endPos;
              } else {
                // Если не нашли закрывающую скобку, выходим из цикла
                break;
              }
            }
            
            // Шаг 3: Обрабатываем найденные структуры
            if (foundStates.length === 0) {
              console.warn('[KanbanNode parseHTML] Не найдено ни одной валидной JSON структуры');
              return undefined;
            } else if (foundStates.length === 1) {
              console.log('[KanbanNode parseHTML] Найдена одна валидная JSON структура');
              return foundStates[0];
            } else {
              console.log(`[KanbanNode parseHTML] Найдено ${foundStates.length} валидных JSON структур, объединяем их`);
              // Объединяем все найденные состояния
              return mergeMultipleBoardStates(foundStates);
            }

          } catch (error) {
            console.error(`[KanbanNode parseHTML] Ошибка при обработке data-state:`, error);
            console.error(`[KanbanNode parseHTML] Сырые данные: ${rawData}`);
            console.error(`[KanbanNode parseHTML] Декодированные данные (если есть): ${decodedData}`);
            return undefined; // В случае любой ошибки возвращаем undefined, чтобы Tiptap использовал default
          }
        },
        renderHTML: (attributes) => {
           // Преобразуем объект обратно в строку JSON для HTML
           // Важно: НЕ кодируем HTML сущности здесь, браузер сделает это сам при установке атрибута
           
           // Проверяем и очищаем объект data-state от циклических ссылок
           try {
             let dataState = attributes['data-state'];
             
             // Если data-state это объект, создаем его безопасную копию
             if (dataState && typeof dataState === 'object') {
               console.log(`[KanbanNode renderHTML] data-state является объектом, создаем безопасную копию`);
               // Глубокое клонирование для избежания циклических ссылок
               dataState = JSON.parse(JSON.stringify(dataState));
             }
             
             // Затем безопасно преобразуем в строку
             const dataStateString = typeof dataState === 'string' ? dataState : JSON.stringify(dataState);
             console.log(`[KanbanNode renderHTML] Рендеринг data-state: ${dataStateString.substring(0,100)}...`);
             
             return {
               'data-state': dataStateString,
             };
           } catch (error) {
             console.error(`[KanbanNode renderHTML] Ошибка при сериализации data-state:`, error);
             // В случае ошибки возвращаем пустую строку, чтобы не допустить ошибку рендеринга
             return {
               'data-state': '{}',
             };
           }
        },
      },
      updateCounter: {
        default: 0,
        parseHTML: (element) => {
          const counter = element.getAttribute('update-counter');
          return counter ? parseInt(counter, 10) : 0;
        },
        renderHTML: (attributes) => {
          return {
            'update-counter': attributes.updateCounter
          };
        }
      },
      id: {
        default: null,
        parseHTML: (element) => {
          const id = element.getAttribute('id');
          if (id) {
            console.log(`[KanbanNode parseHTML] Парсинг ID канбана из HTML: ${id}`);
            return id;
          }
          const newId = `kanban-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`;
          console.warn(`[KanbanNode parseHTML] ID не найден в HTML атрибуте 'id', генерируем новый: ${newId}`);
          return newId;
        },
        renderHTML: (attributes) => {
          // Убеждаемся, что ID всегда есть
          const id = attributes.id || `kanban-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`;
           if (!attributes.id) {
              console.warn(`[KanbanNode renderHTML] ID не был предоставлен, генерируем новый: ${id}`);
           }
          console.log(`[KanbanNode renderHTML] Рендеринг ID канбана: ${id}`);
          return { id };
        },
      },
    };
  },

  // Функция для парсинга HTML
  parseHTML() {
    return [
      // Убедимся, что селекторы соответствуют renderHTML
      {
        tag: 'div[data-component="true"][data-type="kanban"]', // Оставляем для совместимости со старыми данными
      },
      {
        tag: 'div[data-component="true"][data-type="kanbanBoard"]', // Новый формат для соответствия имени узла
      },
      {
        tag: 'div.interactive-kanban', // Оставляем для совместимости, если где-то используется класс
      }
    ];
  },

  // Функция для рендеринга HTML
  renderHTML({ HTMLAttributes }) {
    // Убеждаемся, что ID всегда присутствует ПЕРЕД слиянием атрибутов
    let finalId = HTMLAttributes.id;
    if (!finalId) {
      finalId = `kanban-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`;
      console.warn(`[KanbanNode renderHTML] Генерируем ID при рендеринге HTML, так как он отсутствует: ${finalId}`);
    }

    // Собираем атрибуты для рендеринга
    const finalAttributes = mergeAttributes(HTMLAttributes, {
      'id': finalId, // Передаем финальный ID
      'data-component': 'true',
      'data-type': 'kanbanBoard', // Используем kanbanBoard вместо kanban для соответствия имени узла
      'class': 'interactive-kanban', // Добавляем класс для стилизации
    });

    // Очищаем потенциально пустой data-state, если он undefined
    if (finalAttributes['data-state'] === undefined) {
        delete finalAttributes['data-state'];
    } else if (typeof finalAttributes['data-state'] !== 'string') {
       // Если data-state не строка (например, объект по умолчанию), сериализуем его
       try {
          finalAttributes['data-state'] = JSON.stringify(finalAttributes['data-state']);
       } catch (e) {
          console.error("[KanbanNode renderHTML] Ошибка сериализации data-state по умолчанию", e);
          delete finalAttributes['data-state']; // Удаляем в случае ошибки
       }
    }

    console.log('[KanbanNode renderHTML] Финальные атрибуты для рендеринга:', finalAttributes);
    console.log('[KanbanNode renderHTML] HTML будет выглядеть так:', `<div id="${finalId}" data-component="true" data-type="kanbanBoard" class="interactive-kanban" ...>`);

    return ['div', finalAttributes]; // Возвращаем только один элемент div
  },

  // Добавляем поддержку для отображения в редакторе
  addNodeView() {
    return ReactNodeViewRenderer(KanbanBoardComponent);
  },

  // Добавление плагинов
  addProseMirrorPlugins() {
    const plugins: Plugin[] = [];
    
    // Добавляем стили для кнопки удаления канбана
    if (typeof document !== 'undefined') {
      const styleId = 'kanban-delete-button-styles';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
          .interactive-kanban {
            position: relative;
          }
          
          .interactive-kanban::before {
            content: "✕";
            position: absolute;
            top: 8px;
            right: 8px;
            width: 24px;
            height: 24px;
            background-color: rgba(255, 255, 255, 0.8);
            color: #EF4444;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 16px;
            opacity: 0;
            transition: opacity 0.2s ease;
            z-index: 100;
          }
          
          .dark .interactive-kanban::before {
            background-color: rgba(30, 41, 59, 0.8);
          }
          
          .interactive-kanban:hover::before {
            opacity: 1;
          }
        `;
        document.head.appendChild(style);
      }
    }
    
    // Добавление плагина для правильного взаимодействия с редактором и обработки удаления
    plugins.push(
      new Plugin({
        key: new PluginKey('kanbanBoardHandling'),
        props: {
          handleDOMEvents: {
            mousedown: (view, event) => {
              // Если клик происходит вне узла kanban - не мешаем стандартному поведению
              const target = event.target as HTMLElement;
              const kanbanNode = target.closest('[data-component][data-type="kanban"], [data-component][data-type="kanbanBoard"]');
              
              if (!kanbanNode) {
                return false; // Не наш клик, пусть его обрабатывает редактор
              }
              
              // Проверяем, был ли клик по псевдоэлементу кнопки удаления
              // Поскольку мы не можем напрямую обработать клик по псевдоэлементу,
              // проверяем координаты клика относительно верхнего правого угла канбана
              const rect = kanbanNode.getBoundingClientRect();
              const isDeleteButtonClick = 
                event.clientX >= rect.right - 32 && // 24px ширина кнопки + 8px отступ
                event.clientX <= rect.right - 8 &&
                event.clientY >= rect.top + 8 &&
                event.clientY <= rect.top + 32; // 24px высота кнопки + 8px отступ
              
              if (isDeleteButtonClick) {
                // Находим узел канбан-доски в документе редактора
                const { state } = view;
                const { doc } = state;
                let kanbanPos = -1;
                let nodeSize = 0;
                
                // Найдем позицию узла канбан-доски в документе
                doc.nodesBetween(0, doc.content.size, (node, pos) => {
                  if (node.type.name === 'kanbanBoard' && 
                      kanbanNode.id === node.attrs.id) {
                    kanbanPos = pos;
                    nodeSize = node.nodeSize;
                    return false; // Останавливаем поиск
                  }
                  return true;
                });
                
                if (kanbanPos !== -1) {
                  // Удаляем узел канбана из документа
                  view.dispatch(
                    state.tr.delete(kanbanPos, kanbanPos + nodeSize)
                  );
                  
                  // Устанавливаем фокус на редактор
                  view.focus();
                  
                  // Предотвращаем дальнейшую обработку клика
                  event.stopPropagation();
                  event.preventDefault();
                  return true;
                }
              }
              
              // Если клик произошел ниже канбан-доски или в специальной области ::after
              if (event.clientY > rect.bottom - 30) {
                // Проверяем - находится ли курсор в пределах 30px от нижней границы
                const afterArea = event.clientY <= rect.bottom && event.clientY > rect.bottom - 30;
                
                if (afterArea) {
                  // Если клик в пределах ::after области, создаем новый параграф после
                  // Находим узел канбан-доски в документе редактора
                  const { state } = view;
                  const { doc, selection } = state;
                  let kanbanPos = -1;
                  let nodeSize = 0;
                  
                  // Найдем позицию узла канбан-доски в документе
                  doc.nodesBetween(0, doc.content.size, (node, pos) => {
                    if (node.type.name === 'kanbanBoard') {
                      kanbanPos = pos;
                      nodeSize = node.nodeSize;
                      return false; // Останавливаем поиск
                    }
                    return true;
                  });
                  
                  if (kanbanPos !== -1) {
                    // Вычисляем позицию после узла канбан-доски
                    const posAfter = kanbanPos + nodeSize;
                    
                    // Устанавливаем выделение после канбан-доски
                    view.dispatch(
                      state.tr.setSelection(
                        TextSelection.create(state.doc, posAfter, posAfter)
                      )
                    );
                    
                    // Устанавливаем фокус на редактор
                    view.focus();
                    
                    // Предотвращаем дальнейшую обработку клика
                    event.stopPropagation();
                    event.preventDefault();
                    return true;
                  }
                }
                
                // Не обрабатываем клик, позволяем редактору создать новый параграф
                return false;
              }
              
              // Если клик внутри канбан-доски, обрабатываем его в компоненте
              return false; // Пусть компонент обработает клик
            }
          }
        }
      })
    );
    
    // Добавляем плагин для обработки перетаскивания канбана
    plugins.push(
      new Plugin({
        key: new PluginKey('kanbanDragDrop'),
        props: {
          handleDrop: (view, event, slice, moved) => {
            try {
              console.log('[КанбанДроп] Получено событие drop', event);
              
              // Проверяем, есть ли у нас данные для перетаскивания
              if (!event.dataTransfer) {
                console.log('[КанбанДроп] Нет dataTransfer в событии');
                return false;
              }
              
              // Выводим все доступные типы данных
              console.log('[КанбанДроп] Доступные типы данных:', [...event.dataTransfer.types]);
              
              // Получаем данные для перетаскивания
              let jsonData;
              try {
                jsonData = event.dataTransfer.getData('application/tiptap-node');
                console.log('[КанбанДроп] Получены данные application/tiptap-node:', jsonData);
                
                // Если основные данные не получены, пробуем получить текстовые данные
                if (!jsonData) {
                  const textData = event.dataTransfer.getData('text/plain');
                  console.log('[КанбанДроп] Получены данные text/plain:', textData);
                  
                  if (textData === 'kanbanBoard') {
                    console.log('[КанбанДроп] Это перетаскивание канбана, но данные не найдены');
                    return false;
                  }
                }
              } catch (err) {
                console.error('[КанбанДроп] Ошибка при получении данных:', err);
                return false;
              }
              
              if (!jsonData) {
                console.log('[КанбанДроп] Нет данных для перетаскивания');
                return false;
              }
              
              let data;
              try {
                data = JSON.parse(jsonData);
              } catch (err) {
                console.error('[КанбанДроп] Ошибка при парсинге JSON:', err);
                return false;
              }
              
              console.log('[КанбанДроп] Данные для перетаскивания:', data);
              
              // Проверяем, что это именно канбан-доска
              if (data.type !== 'kanbanBoard') {
                console.log('[КанбанДроп] Тип узла не kanbanBoard:', data.type);
                return false;
              }
              
              // Находим позицию для вставки
              const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
              if (!coords) {
                console.log('[КанбанДроп] Не удалось определить позицию курсора');
                return false;
              }
              
              const { pos } = coords;
              console.log(`[КанбанДроп] Позиция курсора: ${pos}`);
              
              // Получаем текущее состояние и документ
              const { state } = view;
              const { doc, schema } = state;
              const $pos = doc.resolve(pos);
              
              // Находим ближайшую точку вставки между параграфами или в начало/конец документа
              let insertPos = pos;
              
              // Проверка: находимся ли мы в начале параграфа, блока или в конце
              const isAtStart = $pos.parentOffset === 0;
              const isAtEnd = $pos.parentOffset === $pos.parent.content.size;
              
              if (isAtStart) {
                // Вставка перед текущим узлом
                insertPos = $pos.before($pos.depth);
                console.log(`[КанбанДроп] Вставка перед текущим узлом: ${insertPos}`);
              } else if (isAtEnd) {
                // Вставка после текущего узла
                insertPos = $pos.after($pos.depth);
                console.log(`[КанбанДроп] Вставка после текущего узла: ${insertPos}`);
              } else {
                // Вставка внутри текущего узла - разбиваем его
                insertPos = pos;
                console.log(`[КанбанДроп] Вставка внутри текущего узла: ${insertPos}`);
              }
              
              // Получаем узел канбана, который мы хотим переместить
              const sourcePos = data.pos;
              const sourceNodeSize = data.nodeSize;
              
              console.log(`[КанбанДроп] Исходная позиция: ${sourcePos}, размер: ${sourceNodeSize}`);
              
              // Проверяем, что позиция в пределах документа
              if (sourcePos < 0 || sourcePos >= doc.content.size) {
                console.error(`[КанбанДроп] Позиция ${sourcePos} вне пределов документа размером ${doc.content.size}`);
                return false;
              }
              
              // Вместо получения узла из документа используем переданный узел из данных перетаскивания
              let sourceNode;
              if (data.node) {
                try {
                  // Создаем узел из сохраненных данных
                  sourceNode = schema.nodeFromJSON(data.node);
                  console.log('[КанбанДроп] Узел восстановлен из JSON данных');
                } catch (err) {
                  console.error('[КанбанДроп] Ошибка при создании узла из JSON:', err);
                  // Продолжаем с получением узла напрямую из документа
                  sourceNode = doc.nodeAt(sourcePos);
                }
              } else {
                // Получаем узел напрямую из документа, если он не был передан в данных
                sourceNode = doc.nodeAt(sourcePos);
              }
              
              if (!sourceNode) {
                console.error('[КанбанДроп] Исходный узел не найден');
                return false;
              }
              
              console.log(`[КанбанДроп] Тип исходного узла: ${sourceNode.type.name}`);
              
              if (sourceNode.type.name !== 'kanbanBoard') {
                console.error(`[КанбанДроп] Исходный узел не является канбан-доской: ${sourceNode.type.name}`);
                return false;
              }
              
              // Создаем транзакцию для перемещения узла
              const tr = state.tr;
              
              // Если переносим в то же самое место или внутрь самого себя - отменяем
              if (
                sourcePos === insertPos || 
                (insertPos > sourcePos && insertPos < sourcePos + sourceNodeSize)
              ) {
                console.log('[КанбанДроп] Перемещение в то же место или внутрь себя');
                return false;
              }
              
              // Защита от некорректного значения sourceNodeSize
              const actualNodeSize = sourceNode.nodeSize || 1;
              
              // Корректируем позицию вставки, учитывая удаление исходного узла
              const fixedInsertPos = insertPos > sourcePos ? insertPos - actualNodeSize : insertPos;
              console.log(`[КанбанДроп] Скорректированная позиция вставки: ${fixedInsertPos}`);
              
              // Пытаемся использовать replaceWith вместо delete+insert для более атомарной операции
              try {
                // Сначала удаляем, затем вставляем
                tr.delete(sourcePos, sourcePos + actualNodeSize);
                tr.insert(fixedInsertPos, sourceNode);
                
                // Применяем транзакцию
                view.dispatch(tr);
                console.log('[КанбанДроп] Транзакция успешно применена');
                
                // Отмечаем, что мы обработали событие
                event.preventDefault();
                return true;
              } catch (trError) {
                console.error('[КанбанДроп] Ошибка при применении транзакции:', trError);
                
                // Запасной вариант: создаем новую транзакцию и делаем более простую операцию
                try {
                  const newTr = state.tr;
                  // Создаем узел с базовыми атрибутами
                  const newNode = schema.nodes.kanbanBoard.create(sourceNode.attrs);
                  
                  // Просто вставляем новый узел в позицию вставки без удаления
                  newTr.insert(insertPos, newNode);
                  view.dispatch(newTr);
                  
                  // Теперь удаляем старый узел в отдельной транзакции
                  const deleteTr = view.state.tr;
                  deleteTr.delete(sourcePos > insertPos ? sourcePos + newNode.nodeSize : sourcePos, 
                                 sourcePos > insertPos ? sourcePos + sourceNodeSize + newNode.nodeSize : sourcePos + sourceNodeSize);
                  view.dispatch(deleteTr);
                  
                  console.log('[КанбанДроп] Применен запасной вариант транзакции');
                  
                  event.preventDefault();
                  return true;
                } catch (fallbackError) {
                  console.error('[КанбанДроп] Ошибка при запасном варианте транзакции:', fallbackError);
                  return false;
                }
              }
            } catch (error) {
              console.error('[КанбанДроп] Ошибка при обработке перетаскивания канбана:', error);
              return false;
            }
          }
        }
      })
    );
    
    return plugins;
  },

  // Настройки для кнопки добавления в меню
  addOptions() {
    return {
      buttonIcon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line></svg>',
      buttonLabel: 'Канбан-доска',
    };
  }
}); 